import { computed, onScopeDispose, shallowRef, toValue, watch } from "vue";
import { microPinKey, microPinParams } from "#ce/apps/SpFinderMap/lib/micro-pin-params";

/** Owns auxiliary map requests; filter changes cancel coordinates and each click supersedes prior pin info. */
export function useFinderMapPins({ api, params, enabled = true, generation }) {
  const coordinates = shallowRef();
  const pin = shallowRef();
  const loading = shallowRef(false);
  const pinLoading = shallowRef(false);
  const error = shallowRef();
  const pinError = shallowRef();
  let coordinateController;
  let pinController;
  let disposed = false;

  function clearPin() {
    pinController?.abort();
    pinController = undefined;
    pin.value = undefined;
    pinError.value = undefined;
    pinLoading.value = false;
  }

  async function refresh() {
    coordinateController?.abort();
    coordinateController = undefined;
    coordinates.value = undefined;
    error.value = undefined;
    loading.value = false;
    clearPin();
    if (disposed || !toValue(enabled) || !toValue(api) || !toValue(params)) {
      return;
    }
    const controller = new AbortController();
    coordinateController = controller;
    loading.value = true;
    try {
      const data = await toValue(api).coordinates(microPinParams(toValue(params)), { signal: controller.signal });
      if (!controller.signal.aborted) {
        coordinates.value = data;
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        error.value = failure;
      }
    } finally {
      if (coordinateController === controller) {
        loading.value = false;
      }
    }
  }

  async function selectPin(addressId) {
    clearPin();
    if (disposed || !toValue(enabled) || !toValue(api) || addressId == null) {
      return;
    }
    const controller = new AbortController();
    pinController = controller;
    pinLoading.value = true;
    try {
      const data = await toValue(api).pinInfo(addressId, { signal: controller.signal });
      if (!controller.signal.aborted) {
        pin.value = data;
        return data;
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        pinError.value = failure;
      }
    } finally {
      if (pinController === controller) {
        pinLoading.value = false;
      }
    }
  }

  function dispose() {
    disposed = true;
    coordinateController?.abort();
    coordinates.value = undefined;
    loading.value = false;
    clearPin();
  }

  function paramsKey() {
    const value = toValue(params);
    return value ? microPinKey(value) : undefined;
  }

  watch([() => toValue(api), paramsKey, () => toValue(enabled)], refresh, { immediate: true });
  watch(() => toValue(generation), clearPin);
  onScopeDispose(dispose);
  return {
    coordinates: computed(() => coordinates.value),
    pin: computed(() => pin.value),
    loading: computed(() => loading.value),
    pinLoading: computed(() => pinLoading.value),
    error: computed(() => error.value),
    pinError: computed(() => pinError.value),
    refresh,
    selectPin,
    clearPin,
  };
}
